import { Plugin } from 'vite';
import * as path from 'path';
import fs from 'fs-extra';
import { fileURLToPath } from 'url';

// ES module compatible way to get __dirname
const __dirname = path.dirname(fileURLToPath(import.meta.url));
const appVuePath = path.resolve(__dirname, '../../src/App.vue'); // Correctly resolve App.vue from package root

// Helper to only write files when content changes to avoid unnecessary HMR triggers
async function writeFileIfChanged(filePath: string, content: string): Promise<boolean> {
	try {
		const exists = await fs.pathExists(filePath);
		if (exists) {
			const existingContent = await fs.readFile(filePath, 'utf-8');
			if (existingContent === content) {
				return false; // No change needed
			}
		}
		await fs.writeFile(filePath, content, 'utf-8');
		return true; // File was written
	} catch (error) {
		// If there's an error reading, just write the file
		await fs.writeFile(filePath, content, 'utf-8');
		return true;
	}
}

async function generateMainEntry(cwd: string) {
	const entryDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(entryDir);

	const mainContent = `// Auto-generated Frame entry point
// This file is automatically created and managed by Frame
import { createApp } from 'vue'
import { createHead } from '@vueuse/head'
import App from '../App.vue'
import router from './routes'
import { createPinia } from 'pinia'
import { useFrameMeta, initRouterMeta } from './meta-helpers'

// Create the Vue application
const app = createApp(App)

// Add Head management for meta tags
const head = createHead()
app.use(head)

// Add Pinia for state management
const pinia = createPinia()
app.use(pinia)

// Add Vue Router
app.use(router)

// Initialize router-based meta management
// This sets up navigation guards to automatically update meta tags on route changes
initRouterMeta(router)

// Mount the application
app.mount('#app')

// Initialize default meta tags from config
// This ensures defaults are always applied, even if views don't call useFrameMeta()
// Views can still override by calling useFrameMeta() with their own options
useFrameMeta()

// HMR support
if (import.meta.hot) {
	import.meta.hot.accept('../App.vue', (newModule) => {
		console.log('[Frame HMR] App.vue updated')
	})
}
`;

	const mainPath = path.join(entryDir, 'main.ts');
	await writeFileIfChanged(mainPath, mainContent);
}

async function generateAppComponent(cwd: string) {
	const appPath = path.join(cwd, 'src', 'App.vue');

	// Only generate if user hasn't provided their own App.vue
	if (!(await fs.pathExists(appPath))) {
		// Try to copy from package App.vue first
		if (await fs.pathExists(appVuePath)) {
			await fs.copy(appVuePath, appPath);
			console.log('[Frame Entry] Copied default App.vue component');
		} else {
			// Fallback to inline App.vue generation
			const appContent = `<template lang="pug">
#app
  nav.frame-nav(v-if="showHeader")
    router-link(to="/") Home
    router-link(to="/about") About
		
  main.frame-main
    router-view
		
  footer.frame-footer(v-if="showFooter")
    p Powered by 
      strong Frame
      |  🚀
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();
const routerReady = ref(false);

// Computed properties to control header/footer visibility
// Only show after router is ready and check meta flags
const showHeader = computed(() => routerReady.value && !route.meta.hideHeader);
const showFooter = computed(() => routerReady.value && !route.meta.hideFooter);

onMounted(async () => {
  // Wait for router to be ready before showing header/footer
  // This prevents a flash of header/footer on pages that should hide them
  await router.isReady();
  routerReady.value = true;
});
</script>

<style scoped>
.frame-nav {
	padding: 1rem;
	background: #f5f5f5;
	display: flex;
	gap: 1rem;
}

.frame-nav a {
	color: #2563eb;
	text-decoration: none;
	font-weight: 500;
}

.frame-nav a:hover {
	text-decoration: underline;
}

.frame-nav a.router-link-active {
	color: #dc2626;
}

.frame-main {
	padding: 2rem;
	min-height: 50vh;
}

.frame-footer {
	padding: 1rem;
	background: #f9fafb;
	text-align: center;
	color: #6b7280;
	border-top: 1px solid #e5e7eb;
}
</style>
`;

			await fs.writeFile(appPath, appContent);
			console.log('[Frame Entry] Generated default App.vue component');
		}
	}
}

async function generateIndexHtml(cwd: string) {
	const indexHtmlPath = path.join(cwd, 'index.html');

	// Only generate if user hasn't provided their own index.html
	if (!(await fs.pathExists(indexHtmlPath))) {
		const indexHtmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Frame App</title>
  <meta name="description" content="A Vue.js application built with Frame">
  
  <!-- Open Graph -->
  <meta property="og:type" content="website">
  <meta property="og:site_name" content="Frame App">
  <meta property="og:title" content="Frame App">
  <meta property="og:description" content="A Vue.js application built with Frame">
  
  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="Frame App">
  <meta name="twitter:description" content="A Vue.js application built with Frame">
  
  <!-- Theme -->
  <meta name="theme-color" content="#42b883">
  
  <!-- Favicon -->
  <link rel="icon" href="/favicon.ico" type="image/x-icon">
</head>
<body>
  <div id="app"></div>
  <script type="module" src="/src/.frame/main.ts"></script>
</body>
</html>
`;

		await writeFileIfChanged(indexHtmlPath, indexHtmlContent);
		console.log('[Frame Entry] Generated default index.html');
	}
}

export function createEntryPlugin(cwd: string): Plugin {
	return {
		name: 'frame-entry',

		async buildStart() {
			// Generate main.ts entry point
			await generateMainEntry(cwd);

			// Generate default App.vue if it doesn't exist
			await generateAppComponent(cwd);

			// Generate default index.html if it doesn't exist
			await generateIndexHtml(cwd);

			console.log('[Frame Entry] Generated application entry points');
		}
	};
} 